import React from 'react'
import {BoxProps, HStack, useMultiStyleConfig, VStack, Text, Box} from '@chakra-ui/react'
import {Button, ColorCircle} from '_uiComponents'
import {ProductVariationGroup} from '_types'

interface ProductColorSelectorProps {
    variationGroups: Array<ProductVariationGroup>
    selectedColorCode?: string
    onColorSelected: (colorCode: string, variationGroup: ProductVariationGroup) => void
}

/**
 * @name ProductColorSelector
 *
 * @component
 *
 * @description
 * Renders a list of available colours inside a drawer, allowing the user
 * to select a colour variation group for the product.
 *
 * @remarks
 * Follows the same pattern as ProductSizeSelector.
 *
 * @returns {React.FC} - The rendered colour selector component.
 */
const ProductColorSelector: React.FC<ProductColorSelectorProps> = ({variationGroups, selectedColorCode, onColorSelected}: ProductColorSelectorProps) => {
    const styles = useMultiStyleConfig('ProductColorSelector', {}) as Record<string, BoxProps>

    const handleColorItemClick = (colorCode: string, variationGroup: ProductVariationGroup) => {
        onColorSelected(colorCode, variationGroup)
    }

    return (
        <VStack {...styles.container}>
            <VStack {...styles.colorsWrapper}>
                {variationGroups.map((variationGroup, index) => {
                    const {color} = variationGroup
                    const isSelected = color.code === selectedColorCode

                    return (
                        <HStack {...styles.colorItemWrapper} key={`color-item-${variationGroup.ID ?? color.code ?? index}`}>
                            <Button
                                sx={{...styles.colorItemButton, ...(isSelected && styles.colorItemButtonSelected)}}
                                variant="text"
                                onClick={() => {
                                    handleColorItemClick(color.code!, variationGroup)
                                }}
                            >
                                <Box {...styles.swatchItem} aria-hidden="true">
                                    <ColorCircle hexCodes={variationGroup.color.colorHexCode ?? null} selected={isSelected} />
                                </Box>
                                <Text {...styles.colorItemLabel} {...(isSelected && styles.colorItemLabelSelected)}>
                                    {color.label}
                                </Text>
                            </Button>
                        </HStack>
                    )
                })}
            </VStack>
        </VStack>
    )
}

export default ProductColorSelector
